(ns pfo.auth.model (:require
                    ajax.core
                    [clojure.string :as str]
                    [clojure.set]
                    [pfo.state :as state]
                    [pfo.flag :refer [document-queue-enabled?]]
                    [pfo.util :as util]
                    [ajax.formats :refer [raw-response-format]]
                    [promesa.core :as p]
                    [pfo.api :as api]))

(def user-type-family-admin "gr")
(def ^:deprecated user-type-group-admin user-type-family-admin)
(def user-type-license-admin "lic")
(def user-type-regular "r")
(def user-type-contact "c")

(defn family-permissions []
  ;; https://www.pivotaltracker.com/story/show/184615065
  ;; introduced a change to data structure stored in local storage.
  ;; The data is recreated only on login, which could cause a front end
  ;; error difficult to recover from. The condition below detects the
  ;; change and forces logout.
  (if (and
       (seq @state/family-permissions)
       (not (set? (first (vals @state/family-permissions)))))
    (do
      (util/set-location "#/logout")
      nil)
    @state/family-permissions))

(def ^:deprecated group-permissions family-permissions)

(defn has-family-perm [family-id perm-type]
  (boolean (get-in (family-permissions) [(util/str->int family-id) perm-type])))

(defn has-any-of-perms-in-family [family-id perm-type-set]
  (some (fn [perm-type] (has-family-perm family-id perm-type)) perm-type-set))

(defn has-perm-in-any-family [perm-type]
  (contains? (reduce clojure.set/union #{} (vals (family-permissions))) perm-type))

(defn filter-families [families perm]
  (filter #(has-family-perm (:id %) perm) families))

(defn is-user-family-admin [family-id] (has-family-perm family-id :is_family_admin))

(defn ^:deprecated is-user-group-admin
  "DEPRECATED. Use is-user-family-admin instead"
  [family-id]
  (is-user-family-admin family-id))

(defn is-user-regular [family-id]
  (not (is-user-family-admin family-id)))

(defn is-user-any-family-admin []
  (contains? (reduce clojure.set/union (vals (family-permissions))) :is_family_admin))

(defn ^:deprecated is-user-any-group-admin
  "DEPRECATED. Use is-user-any-family-admin instead"
  []
  (is-user-any-family-admin))

(defn can-access-document-review?
  "Whether the user may reach the document review queue: a family admin of any
  family, or a designated primary/secondary document reviewer. Mirrors the
  server's review-access check (perm.userCanReviewDocuments); the reviewer half
  is delivered as :can_review_documents in the group_permissions response since
  it is derived from reviewer routing, not a grantable permission.

  Gated on the mobile-app and novice switches (#3008, #3107): the queue holds
  only mobile and novice uploads, so its desktop entrypoints stay hidden until
  one of them ships. The backend endpoints are deliberately left ungated —
  the mobile app itself still uses them."
  []
  (and (document-queue-enabled?)
       (or (is-user-any-family-admin)
           (boolean @state/can-review-documents))))

(defn show-my-uploads?
  "Whether to show the user's own queue uploads: the server marks the user
  Novice (#3106) and the review queue switch is on. Not read from family
  permissions, which also list inactive families."
  []
  (and (document-queue-enabled?)
       (boolean @state/is-novice)))

(defn only-admin-families [families]
  (filterv #(is-user-family-admin (:id %)) families))

(def ^:deprecated only-admin-groups only-admin-families)

(defn is-user-license-admin []
  (= user-type-license-admin (api/get-auth-info :user-type)))

(defn is-user-admin []
  (or (is-user-license-admin) (is-user-any-family-admin)))

(defn has-perm [perm-type]
  (contains? @state/permission-types (name perm-type)))

(defn is-user-subscriber-admin [subscriber-id]
  (contains? @state/subscriber-admin (util/str->int subscriber-id)))

(defn is-user-any-subscriber-admin [] (seq @state/subscriber-admin))

(defn is-user-any-admin []
  (or
   (is-user-any-family-admin)
   (is-user-any-subscriber-admin)
   (is-user-license-admin)))

(defn get-full-name
  ([{first-name :first_name
     last-name :last_name
     middle-name :middle_name
     suffix :suffix
     company-name :company_name
     linked-data :linked_data
     :as user} sortable]
   (str
    (when (= linked-data "incomplete") "(")
    (if (and (:is_automation user) (empty? first-name) (empty? last-name))
      "Document Automation"
      (if (or (not-empty first-name) (not-empty last-name))
        (if sortable
          (str/join ", " (filter not-empty [last-name (str/join " " (filter not-empty [first-name middle-name suffix]))]))
          (str/join " " (filter not-empty [first-name middle-name last-name suffix])))
        company-name))
    (when (= linked-data "incomplete") ")")))
  ([user] (get-full-name user false)))

(defn timezone-list []
  (api/ajaxp (str "/timezones/") {}))

(defn profile-details []
  (api/ajaxp "/profile/" {}))

(defn profile-update [form-data]
  (api/ajaxp "/profile/" {:method :post
                          :body (util/make-params (util/remove-nil-values form-data))}))

(defn invitation-details [uid]
  (api/ajaxp (str "/invitations/" uid "/") {}))

(defn invitation-accept [uid]
  (api/ajaxp (str "/invitations/" uid "/accept/") {:method :post}))

(defn invitation-reject [uid]
  (api/ajaxp (str "/invitations/" uid "/reject/") {:method :post}))

(defn password-reset-request [email]
  (api/ajaxp "/profile/password_reset/init/" {:method :post
                                              :params (util/remove-nil-values {:email email})}))

(defn password-reset [token password]
  (api/ajaxp "/profile/password_reset/" {:method :post
                                         :params (util/remove-nil-values {:token token
                                                                          :password password
                                                                          :response-format (raw-response-format)})}))

(defn mfa-reset-init [current-token]
  (api/ajaxp "/profile/mfa/init/" {:method :post
                                   :params (util/remove-nil-values {:code current-token})}))

(defn mfa-reset-submit [code-url code]
  (api/ajaxp code-url {:method :post
                       :params {:code @code}}))

(defn validate-reset-request [token]
  (api/ajaxp "/profile/password_reset/validate/" {:method :post
                                                  :params (util/remove-nil-values {:token token})}))

(defn perm
  ([perm-type component] (when (has-perm perm-type) component))
  ([perm-type object component] (when (get-in object [:perms perm-type]) component)))

(defn support-sso []
  (api/ajaxp "/profile/support/sso/" {:method :post
                                      :response-format (api/raw-response-format)}))

(defn permission-types [collection-ids]
  (->> (api/ajaxp "/permission_types/" {:params (util/remove-nil-values {:collection_id collection-ids})})
       (p/map #(into #{} (map keyword %)))))
